/*
 * Copyright 2019 Teralytics
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 */
var __values = (this && this.__values) || function(o) {
    var s = typeof Symbol === "function" && Symbol.iterator, m = s && o[s], i = 0;
    if (m) return m.call(o);
    if (o && typeof o.length === "number") return {
        next: function () {
            if (o && i >= o.length) o = void 0;
            return { value: o && o[i++], done: !o };
        }
    };
    throw new TypeError(s ? "Object is not iterable." : "Symbol.iterator is not defined.");
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import { ascending, bisectLeft, extent } from 'd3-array';
import { isCluster } from './types';
/**
 * Build ClusterIndex from the given cluster hierarchy
 */
export function buildIndex(clusterLevels) {
    var e_1, _a, e_2, _b, e_3, _c;
    var nodesByZoom = new Map();
    var clustersById = new Map();
    var minZoomByLocationId = new Map();
    try {
        for (var clusterLevels_1 = __values(clusterLevels), clusterLevels_1_1 = clusterLevels_1.next(); !clusterLevels_1_1.done; clusterLevels_1_1 = clusterLevels_1.next()) {
            var _d = clusterLevels_1_1.value, zoom = _d.zoom, nodes = _d.nodes;
            nodesByZoom.set(zoom, nodes);
            try {
                for (var nodes_1 = (e_2 = void 0, __values(nodes)), nodes_1_1 = nodes_1.next(); !nodes_1_1.done; nodes_1_1 = nodes_1.next()) {
                    var node = nodes_1_1.value;
                    if (isCluster(node)) {
                        clustersById.set(node.id, node);
                    }
                    else {
                        var id = node.id;
                        var mz = minZoomByLocationId.get(id);
                        if (mz == null || mz > zoom) {
                            minZoomByLocationId.set(id, zoom);
                        }
                    }
                }
            }
            catch (e_2_1) { e_2 = { error: e_2_1 }; }
            finally {
                try {
                    if (nodes_1_1 && !nodes_1_1.done && (_b = nodes_1.return)) _b.call(nodes_1);
                }
                finally { if (e_2) throw e_2.error; }
            }
        }
    }
    catch (e_1_1) { e_1 = { error: e_1_1 }; }
    finally {
        try {
            if (clusterLevels_1_1 && !clusterLevels_1_1.done && (_a = clusterLevels_1.return)) _a.call(clusterLevels_1);
        }
        finally { if (e_1) throw e_1.error; }
    }
    var _e = __read(extent(clusterLevels, function (cl) { return cl.zoom; }), 2), minZoom = _e[0], maxZoom = _e[1];
    if (minZoom == null || maxZoom == null) {
        throw new Error('Could not determine minZoom or maxZoom');
    }
    var leavesToClustersByZoom = new Map();
    var _loop_1 = function (cluster) {
        var zoom = cluster.zoom;
        var leavesToClusters = leavesToClustersByZoom.get(zoom);
        if (!leavesToClusters) {
            leavesToClusters = new Map();
            leavesToClustersByZoom.set(zoom, leavesToClusters);
        }
        visitClusterLeaves(cluster, function (leafId) {
            leavesToClusters.set(leafId, cluster);
        });
    };
    try {
        for (var _f = __values(clustersById.values()), _g = _f.next(); !_g.done; _g = _f.next()) {
            var cluster = _g.value;
            _loop_1(cluster);
        }
    }
    catch (e_3_1) { e_3 = { error: e_3_1 }; }
    finally {
        try {
            if (_g && !_g.done && (_c = _f.return)) _c.call(_f);
        }
        finally { if (e_3) throw e_3.error; }
    }
    function visitClusterLeaves(cluster, visit) {
        var e_4, _a;
        try {
            for (var _b = __values(cluster.children), _c = _b.next(); !_c.done; _c = _b.next()) {
                var childId = _c.value;
                var child = clustersById.get(childId);
                if (child) {
                    visitClusterLeaves(child, visit);
                }
                else {
                    visit(childId);
                }
            }
        }
        catch (e_4_1) { e_4 = { error: e_4_1 }; }
        finally {
            try {
                if (_c && !_c.done && (_a = _b.return)) _a.call(_b);
            }
            finally { if (e_4) throw e_4.error; }
        }
    }
    var expandCluster = function (cluster, targetZoom) {
        if (targetZoom === void 0) { targetZoom = maxZoom; }
        var ids = [];
        var visit = function (c, expandedIds) {
            var e_5, _a;
            if (targetZoom > c.zoom) {
                try {
                    for (var _b = __values(c.children), _c = _b.next(); !_c.done; _c = _b.next()) {
                        var childId = _c.value;
                        var child = clustersById.get(childId);
                        if (child) {
                            visit(child, expandedIds);
                        }
                        else {
                            expandedIds.push(childId);
                        }
                    }
                }
                catch (e_5_1) { e_5 = { error: e_5_1 }; }
                finally {
                    try {
                        if (_c && !_c.done && (_a = _b.return)) _a.call(_b);
                    }
                    finally { if (e_5) throw e_5.error; }
                }
            }
            else {
                expandedIds.push(c.id);
            }
        };
        visit(cluster, ids);
        return ids;
    };
    function findClusterFor(locationId, zoom) {
        var leavesToClusters = leavesToClustersByZoom.get(zoom);
        if (!leavesToClusters) {
            return undefined;
        }
        var cluster = leavesToClusters.get(locationId);
        return cluster ? cluster.id : undefined;
    }
    var availableZoomLevels = clusterLevels.map(function (cl) { return +cl.zoom; }).sort(function (a, b) { return ascending(a, b); });
    return {
        availableZoomLevels: availableZoomLevels,
        getClusterNodesFor: function (zoom) {
            if (zoom === undefined) {
                return undefined;
            }
            return nodesByZoom.get(zoom);
        },
        getClusterById: function (clusterId) { return clustersById.get(clusterId); },
        getMinZoomForLocation: function (locationId) { return minZoomByLocationId.get(locationId) || minZoom; },
        expandCluster: expandCluster,
        findClusterFor: findClusterFor,
        aggregateFlows: function (flows, zoom, _a, options) {
            var e_6, _b;
            var getFlowOriginId = _a.getFlowOriginId, getFlowDestId = _a.getFlowDestId, getFlowMagnitude = _a.getFlowMagnitude;
            if (options === void 0) { options = {}; }
            if (zoom > maxZoom) {
                return flows;
            }
            var result = [];
            var aggFlowsByKey = new Map();
            var makeKey = function (origin, dest) { return origin + ":" + dest; };
            var _c = options.flowCountsMapReduce, flowCountsMapReduce = _c === void 0 ? {
                map: getFlowMagnitude,
                reduce: function (acc, count) { return (acc || 0) + count; },
            } : _c;
            try {
                for (var flows_1 = __values(flows), flows_1_1 = flows_1.next(); !flows_1_1.done; flows_1_1 = flows_1.next()) {
                    var flow = flows_1_1.value;
                    var origin_1 = getFlowOriginId(flow);
                    var dest = getFlowDestId(flow);
                    var originCluster = findClusterFor(origin_1, zoom) || origin_1;
                    var destCluster = findClusterFor(dest, zoom) || dest;
                    var key = makeKey(originCluster, destCluster);
                    if (originCluster === origin_1 && destCluster === dest) {
                        result.push(flow);
                    }
                    else {
                        var aggregateFlow = aggFlowsByKey.get(key);
                        if (!aggregateFlow) {
                            aggregateFlow = {
                                origin: originCluster,
                                dest: destCluster,
                                count: flowCountsMapReduce.map(flow),
                                aggregate: true,
                            };
                            result.push(aggregateFlow);
                            aggFlowsByKey.set(key, aggregateFlow);
                        }
                        else {
                            aggregateFlow.count = flowCountsMapReduce.reduce(aggregateFlow.count, flowCountsMapReduce.map(flow));
                        }
                    }
                }
            }
            catch (e_6_1) { e_6 = { error: e_6_1 }; }
            finally {
                try {
                    if (flows_1_1 && !flows_1_1.done && (_b = flows_1.return)) _b.call(flows_1);
                }
                finally { if (e_6) throw e_6.error; }
            }
            return result;
        },
    };
}
export function makeLocationWeightGetter(flows, _a) {
    var e_7, _b;
    var getFlowOriginId = _a.getFlowOriginId, getFlowDestId = _a.getFlowDestId, getFlowMagnitude = _a.getFlowMagnitude;
    var locationTotals = {
        incoming: new Map(),
        outgoing: new Map(),
    };
    try {
        for (var flows_2 = __values(flows), flows_2_1 = flows_2.next(); !flows_2_1.done; flows_2_1 = flows_2.next()) {
            var flow = flows_2_1.value;
            var origin_2 = getFlowOriginId(flow);
            var dest = getFlowDestId(flow);
            var count = getFlowMagnitude(flow);
            locationTotals.incoming.set(dest, (locationTotals.incoming.get(dest) || 0) + count);
            locationTotals.outgoing.set(origin_2, (locationTotals.outgoing.get(origin_2) || 0) + count);
        }
    }
    catch (e_7_1) { e_7 = { error: e_7_1 }; }
    finally {
        try {
            if (flows_2_1 && !flows_2_1.done && (_b = flows_2.return)) _b.call(flows_2);
        }
        finally { if (e_7) throw e_7.error; }
    }
    return function (id) {
        return Math.max(Math.abs(locationTotals.incoming.get(id) || 0), Math.abs(locationTotals.outgoing.get(id) || 0));
    };
}
/**
 * @param availableZoomLevels Must be sorted in ascending order
 * @param targetZoom
 */
export function findAppropriateZoomLevel(availableZoomLevels, targetZoom) {
    if (!availableZoomLevels.length) {
        throw new Error('No available zoom levels');
    }
    return availableZoomLevels[Math.min(bisectLeft(availableZoomLevels, Math.floor(targetZoom)), availableZoomLevels.length - 1)];
}
//# sourceMappingURL=ClusterIndex.js.map